import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import { itemsOnPage } from 'shared/helpers/itemsOnPage';

import { actions } from 'features/reports/redux';

import convertPlayersDeposits from './convertPlayersDeposits';
import { amountTypes } from '../../../users/data';
import parseNumberFromString from '../../../../shared/helpers/parseNumberFromString';

const date = dayjs().startOf('day');
export const initialValues = {
  id: '',
  username: '',
  from: null,
  to: null,
  itemsOnPage,
  page: 0,
  type: ['all'],
  bonusAmount: 0,
  bonusPercentAmount: '',
  isBonus: '',
};


const usePlayersDeposits = () => {
  const playerDeposits = useSelector(s => s.reports.playerDeposits);
  const actionProcessing = useSelector(s => s.reports.actionProcessing);

  const dispatch = useDispatch();

  const [values, setValues] = useState(() => {
    return initialValues;
  });

  const [activeDay, setActiveDay] = useState('');

  const handleTypeChange = type => {
    if (type === 'all') {
      setValues(ps => ({ ...ps, type: ps.type.includes('all') ? [] : ['all'] }));
    } else {
      setValues(ps => {
        const newTypes = ps.type.includes(type)
          ? ps.type.filter(v => v !== type)
          : [...ps.type.filter(v => v !== 'all'), type];

        return { ...ps, type: newTypes };
      });
    }
  };

  const handleValueChange = (name, value) => {
    setValues(ps => ({
      ...ps,
      [name]: value,
    }));

    if (name === 'from' || name === 'to') setActiveDay('');
  };

  const handleChangeActiveDay = dayFilter => {
    setActiveDay(dayFilter);
    setValues(ps => ({
      ...ps,
      from: dayFilter.value,
      to: dayFilter.value2,
    }));
  };

  const handleRequestChange = (item, status) => {
    dispatch(
      actions.changePlayerDeposit(
        {
          type: item.type,
          id: item.id,
          status,
        },
        () => dispatch(actions.getPlayerDeposits(convertPlayersDeposits(values))),
      ),
    );
  };

  const handlePlayerDepositsSubmit = e => {
    e.preventDefault();
    if (!actionProcessing) {
      dispatch(actions.getPlayerDeposits(convertPlayersDeposits(values)));
    }
  };

  const handleIsBonusChange = useCallback(value => {
    setValues(ps => ({
      ...ps,
      isBonus: value,
    }));
    if (!value) {
      setValues(ps => ({
        ...ps,
        bonusAmount: '',
        bonusPercentAmount: '',
      }));
    }
  }, []);

  const handleBonusAmountChange = useCallback(
    (e, item) => {
      const value = e?.currentTarget?.value ?? e;

      if (e?.currentTarget) {
        setValues(ps => ({
          ...ps,
          bonusAmount: value,
        }));
        dispatch(actions.setDepositBonusPercent({ bonus_percent: Number(value), id: item.id }));
      } else {
        setValues(ps => ({
          ...ps,
          bonusAmount: () => {
            if (!Object.keys(amountTypes).includes(value)) return Number(value);
            return Number(value) + Number(parseNumberFromString(values.bonusAmount));
          },
        }));
      }

      setValues(ps => ({
        ...ps,
        bonusPercentAmount: '',
      }));
    },
    [values.bonusAmount],
  );

  const handleBonusPercentChange = useCallback(e => {
    const value = (e?.currentTarget?.value ?? e).replace('%', '');
    if (e?.currentTarget) {
      setValues(ps => ({
        ...ps,
        bonusAmount: '',
        bonusPercentAmount: value,
      }));
    }
  }, []);

  const pageCount = useMemo(
    () => Math.ceil(playerDeposits.count / Number(values.itemsOnPage.find(s => s.active)?.value ?? 10)) ?? 1,
    [values.itemsOnPage],
  );

  useEffect(() => {
    handlePlayerDepositsSubmit(new Event('submit'));
  }, [values.page, pageCount, values.itemsOnPage]);

  useEffect(() => {
    if ((values.page > pageCount - 1 && pageCount - 1 >= 0) || pageCount === 0) handleValueChange('page', 0);
  }, [values.page, pageCount]);

  const activeItemsOnPage = useMemo(() => values.itemsOnPage.find(i => i.active) ?? 10, [values.itemsOnPage]);

  return {
    state: {
      values,
      activeDay,
    },
    out: {
      items: playerDeposits.data,
      count: playerDeposits.count,
      cbu: playerDeposits.cbu,
      activeItemsOnPage,
    },
    handlers: {
      setActiveDay,
      handleValueChange,
      handleChangeActiveDay,
      handleRequestChange,
      handleTypeChange,
      handlePlayerDepositsSubmit,
      handleIsBonusChange,
      handleBonusAmountChange,
      handleBonusPercentChange,
    },
  };
};

export default usePlayersDeposits;
